///
/// EarthBrowser
///
/// Created by Matt Giger on 10/30/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Local line density over a tile: kilometers of line per square kilometer,
/// binned into a geographic grid and sampled bilinearly per vertex so the
/// line shader can fade out areas where lines would crowd on screen (e.g. a
/// country whose first-level divisions are tiny municipalities).
///

import { GeoPolylineGeometry, type GeoLinePoint } from "@/earthbrowser/entities/VectorTiles/GeoPolylineGeometry"
import { wgs } from "@/earthbrowser/utils/geographic"

/** Geographic bounds in radians */
export interface GeoBounds {
	minLon: number
	maxLon: number
	minLat: number
	maxLat: number
}

// Cells per tile side. Fine enough that a small dense country (Slovenia at
// level 4) fills whole cells instead of being averaged into its neighbors;
// bilinear sampling between cell centers keeps the fade continuous
const cDefaultCells = 16

export class LineDensityGrid {
	private readonly cells: number
	private readonly bounds: GeoBounds
	private readonly cellLon: number
	private readonly cellLat: number
	private values: Float32Array

	constructor(bounds: GeoBounds, cells: number = cDefaultCells) {
		this.cells = cells
		this.bounds = bounds
		this.cellLon = (bounds.maxLon - bounds.minLon) / cells
		this.cellLat = (bounds.maxLat - bounds.minLat) / cells
		this.values = new Float32Array(cells * cells)
	}

	/** Add the length of every segment to the cell under its midpoint */
	public accumulate(lines: GeoLinePoint[][]): void {
		// Split long segments so each piece lands in roughly one cell
		const maxPiece = Math.min(this.cellLon, this.cellLat)
		for (const line of lines) {
			for (let i = 0; i < line.length - 1; i++) {
				const a = line[i]
				const b = line[i + 1]
				const span = Math.max(Math.abs(b.lon - a.lon), Math.abs(b.lat - a.lat))
				const pieces = Math.max(1, Math.ceil(span / maxPiece))
				const pieceKm = GeoPolylineGeometry.distanceKm(a, b) / pieces
				for (let p = 0; p < pieces; p++) {
					const t = (p + 0.5) / pieces
					const index = this.cellIndex(a.lon + (b.lon - a.lon) * t, a.lat + (b.lat - a.lat) * t)
					this.values[index] += pieceKm
				}
			}
		}
	}

	/** Convert accumulated lengths to densities (km/km²) */
	public finalize(): void {
		const n = this.cells
		for (let row = 0; row < n; row++) {
			const latCenter = this.bounds.maxLat - (row + 0.5) * this.cellLat
			const areaKm2 = this.cellLat * wgs.a * this.cellLon * wgs.a * Math.max(Math.cos(latCenter), 0.05)
			for (let col = 0; col < n; col++) {
				this.values[row * n + col] /= areaKm2
			}
		}
	}

	/** Bilinear sample of the density at a position (radians) */
	public sample(lon: number, lat: number): number {
		const n = this.cells
		// Continuous cell coordinates with cell centers at integer + 0.5
		const x = Math.min(Math.max((lon - this.bounds.minLon) / this.cellLon - 0.5, 0), n - 1)
		const y = Math.min(Math.max((this.bounds.maxLat - lat) / this.cellLat - 0.5, 0), n - 1)
		const x0 = Math.floor(x)
		const y0 = Math.floor(y)
		const x1 = Math.min(x0 + 1, n - 1)
		const y1 = Math.min(y0 + 1, n - 1)
		const fx = x - x0
		const fy = y - y0
		const top = this.values[y0 * n + x0] * (1 - fx) + this.values[y0 * n + x1] * fx
		const bottom = this.values[y1 * n + x0] * (1 - fx) + this.values[y1 * n + x1] * fx
		return top * (1 - fy) + bottom * fy
	}

	private cellIndex(lon: number, lat: number): number {
		const n = this.cells
		const col = Math.min(Math.max(Math.floor((lon - this.bounds.minLon) / this.cellLon), 0), n - 1)
		const row = Math.min(Math.max(Math.floor((this.bounds.maxLat - lat) / this.cellLat), 0), n - 1)
		return row * n + col
	}
}
